import React, { useState } from 'react';
import { CHEMICALS_DATA } from '../database';
import type { ChemicalProduct, WaterBodyType } from '../types';
import { annotateUsageWithMetric } from '../utils/dosage';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SPA_CATEGORIES = new Set([
  'Spa Sanitizers',
  'Spa Shocks & Oxidizers',
  'Spa Balancers',
  'Spa Specialty',
]);

// ─── Sub-components ───────────────────────────────────────────────────────────

const ProductCard: React.FC<{ product: ChemicalProduct }> = ({ product }) => (
  <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
    <div className="flex justify-between items-start gap-2">
      <div>
        <p className="font-semibold text-white text-sm">{product.productName}</p>
        <p className="text-xs text-cyan-400">{product.brand}</p>
      </div>
      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full whitespace-nowrap">
        {product.category}
      </span>
    </div>
    <p className="text-xs text-slate-300">{product.description}</p>
    <p className="text-xs text-amber-300 bg-amber-900/30 rounded px-2 py-1">
      💡 {annotateUsageWithMetric(product.usage)}
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

interface ProductGuideTabProps {
  waterBody?: WaterBodyType;
}

export const ProductGuideTab: React.FC<ProductGuideTabProps> = ({ waterBody = 'pool' }) => {
  const isHotTub = waterBody === 'hot_tub';

  // Get unique brands from the database
  const allBrands = ['All', ...Array.from(new Set(CHEMICALS_DATA.map((p) => p.brand))).sort()];
  const allCategories = ['All', ...Array.from(new Set(CHEMICALS_DATA.map((p) => p.category))).sort()];

  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [search, setSearch] = useState('');

  // Default category filter based on water body
  const defaultCategoryFilter = (p: ChemicalProduct): boolean => {
    if (selectedCategory !== 'All') return p.category === selectedCategory;
    if (isHotTub) return SPA_CATEGORIES.has(p.category) || p.category === 'Specialty';
    return true;
  };

  const filtered = CHEMICALS_DATA.filter((p) => {
    const brandMatch = selectedBrand === 'All' || p.brand === selectedBrand;
    const categoryMatch = defaultCategoryFilter(p);
    const searchMatch =
      search === '' ||
      p.productName.toLowerCase().includes(search.toLowerCase()) ||
      p.brand.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase());
    return brandMatch && categoryMatch && searchMatch;
  });

  // Group by category
  const grouped = new Map<string, ChemicalProduct[]>();
  for (const p of filtered) {
    if (!grouped.has(p.category)) grouped.set(p.category, []);
    grouped.get(p.category)!.push(p);
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="bg-slate-800 p-4 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-white text-center">
          {isHotTub ? '♨️ Spa & Hot Tub Product Guide' : '🏊 Pool Product Guide'}
        </h2>
        <p className="text-slate-400 text-sm text-center mt-1">
          {CHEMICALS_DATA.length} products from {allBrands.length - 1} brands
        </p>
        {isHotTub && (
          <p className="text-orange-400 text-xs text-center mt-1">
            Showing spa-specific products — use the category filter to see all pool products too
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-slate-800 p-4 rounded-lg shadow-md space-y-3">
        <input
          type="text"
          placeholder="Search products, brands, or descriptions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white text-sm placeholder-slate-400"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Brand</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white text-sm"
            >
              {allBrands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white text-sm"
            >
              {allCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-xs text-slate-500">
            Showing {filtered.length} of {CHEMICALS_DATA.length} products
          </p>
          <button
            onClick={() => { setSelectedBrand('All'); setSelectedCategory('All'); setSearch(''); }}
            className="text-xs text-cyan-400 hover:text-cyan-300"
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Product Groups */}
      {grouped.size === 0 ? (
        <div className="bg-slate-800 p-8 rounded-lg text-center">
          <p className="text-slate-400">No products match your filters.</p>
        </div>
      ) : (
        Array.from(grouped.entries()).map(([category, products]) => (
          <div key={category} className="bg-slate-800 p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-bold text-cyan-400 mb-3">{category}</h3>
            <div className="space-y-3">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
